/**
 * A small rich-text editor whose value is markdown, built on Plate (the
 * editor mytender's web app uses). Headings, bold, italic, bulleted and
 * numbered lists and links; typing markdown ("## ", "- ", "1. ", "**x**")
 * formats as you go, and selecting text shows a floating toolbar. Used for a
 * question's writing plan, which mytender stores and returns as markdown.
 */
import { BoldPlugin, H1Plugin, H2Plugin, H3Plugin, ItalicPlugin } from "@platejs/basic-nodes/react";
import { BoldRules, HeadingRules, ItalicRules } from "@platejs/basic-nodes";
import { IndentPlugin } from "@platejs/indent/react";
import { LinkPlugin } from "@platejs/link/react";
import { BulletedListRules, OrderedListRules, isOrderedList, someList, toggleList } from "@platejs/list";
import { ListPlugin } from "@platejs/list/react";
import { MarkdownPlugin } from "@platejs/markdown";
import { BoldIcon, Heading2Icon, Heading3Icon, ItalicIcon, ListIcon, ListOrderedIcon } from "@/design/icons";
import { KEYS } from "platejs";
import {
  ParagraphPlugin,
  Plate,
  PlateElement,
  type PlateElementProps,
  useEditorRef,
  useEditorSelector,
  usePlateEditor,
} from "platejs/react";
import { type FC, forwardRef, useImperativeHandle } from "react";
import remarkGfm from "remark-gfm-react-10";

import { cn } from "@/agents-v2/utils/cn";
import { BlockList } from "@/design/ui/block-list";
import { Editor, EditorContainer } from "@/design/ui/editor";
import { FloatingToolbar } from "@/design/ui/floating-toolbar";
import { H1Element, H2Element, H3Element } from "@/design/ui/heading-node";
import { MarkToolbarButton } from "@/design/ui/mark-toolbar-button";
import { ParagraphElement } from "@/design/ui/paragraph-node";
import { ToolbarButton, ToolbarGroup } from "@/design/ui/toolbar";

const LinkElement = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="a"
    className="text-accent-hover underline"
    attributes={{ ...props.attributes, href: (props.element as { url?: string }).url, target: "_blank" }}
  >
    {props.children}
  </PlateElement>
);

const heading = (plugin: typeof H1Plugin, component: FC<PlateElementProps>) =>
  plugin.configure({
    inputRules: [HeadingRules.markdown()],
    node: { component },
    rules: { break: { empty: "reset" } },
  });

const plugins = [
  ParagraphPlugin.withComponent(ParagraphElement),
  heading(H1Plugin, H1Element),
  heading(H2Plugin as typeof H1Plugin, H2Element),
  heading(H3Plugin as typeof H1Plugin, H3Element),
  BoldPlugin.configure({ inputRules: [BoldRules.markdown({ variant: "*" }), BoldRules.markdown({ variant: "_" })] }),
  ItalicPlugin.configure({
    inputRules: [ItalicRules.markdown({ variant: "*" }), ItalicRules.markdown({ variant: "_" })],
  }),
  LinkPlugin.withComponent(LinkElement),
  IndentPlugin.configure({ inject: { targetPlugins: [...KEYS.heading, KEYS.p] }, options: { offset: 20 } }),
  ListPlugin.configure({
    inputRules: [
      BulletedListRules.markdown({ variant: "-" }),
      BulletedListRules.markdown({ variant: "*" }),
      OrderedListRules.markdown({ variant: "." }),
    ],
    // Bulleted items render as list items with a marker; numbered ones are
    // wrapped in an <ol> by BlockList (as Plate's own list kit does).
    inject: {
      nodeProps: {
        nodeKey: KEYS.listType,
        query: ({ nodeProps }) => !!nodeProps.element?.listStyleType && !isOrderedList(nodeProps.element),
        transformProps: ({ props }) => ({
          ...props,
          role: "listitem",
          style: { ...props.style, display: "list-item" },
        }),
      },
      targetPlugins: [...KEYS.heading, KEYS.p],
    },
    render: { belowNodes: BlockList },
  }),
  MarkdownPlugin.configure({ options: { remarkPlugins: [remarkGfm] } }),
];

/** Imperative handle: read the markdown, or put the editor back to some markdown. */
export type MarkdownEditorHandle = { markdown: () => string; reset: (markdown: string) => void };

export type MarkdownEditorProps = {
  /** The starting markdown; later changes go through the handle's reset. */
  initialMarkdown: string;
  placeholder?: string;
  /** Called with the markdown after each change. */
  onChange?: (markdown: string) => void;
  className?: string;
};

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  ({ initialMarkdown, placeholder, onChange, className }, ref) => {
    // Always enabled, so never null.
    const editor = usePlateEditor({
      plugins,
      value: (e) => e.getApi(MarkdownPlugin).markdown.deserialize(initialMarkdown || ""),
    }) as NonNullable<ReturnType<typeof usePlateEditor>>;
    const serialize = () => editor.getApi(MarkdownPlugin).markdown.serialize().trim();

    useImperativeHandle(ref, () => ({
      markdown: serialize,
      reset: (markdown: string) => editor.tf.setValue(editor.getApi(MarkdownPlugin).markdown.deserialize(markdown)),
    }));

    return (
      <Plate editor={editor} onValueChange={() => onChange?.(serialize())}>
        <EditorContainer
          variant="select"
          className={cn(
            "rounded-md border-line bg-surface ring-offset-0 focus-within:border-accent focus-within:ring-0 focus-within:ring-offset-0",
            className
          )}
        >
          <Editor
            variant="none"
            placeholder={placeholder}
            className={cn(
              "min-h-[120px] px-3 py-2 text-base leading-relaxed text-fg-2",
              "[&_[data-slate-placeholder]]:!opacity-100 [&_[data-slate-placeholder]]:text-fg-4",
              "[&_strong]:font-semibold [&_strong]:text-fg"
            )}
          />
          <FloatingToolbar className="border-line bg-surface shadow-pop">
            <PlanToolbar />
          </FloatingToolbar>
        </EditorContainer>
      </Plate>
    );
  }
);
MarkdownEditor.displayName = "MarkdownEditor";

const PlanToolbar = () => {
  const editor = useEditorRef();
  const block = useEditorSelector((e) => e.api.block()?.[0]?.type as string | undefined, []);
  const bulleted = useEditorSelector((e) => someList(e, "disc"), []);
  const numbered = useEditorSelector((e) => someList(e, "decimal"), []);
  return (
    <ToolbarGroup>
      <MarkToolbarButton nodeType={KEYS.bold} tooltip="Bold (⌘B)">
        <BoldIcon />
      </MarkToolbarButton>
      <MarkToolbarButton nodeType={KEYS.italic} tooltip="Italic (⌘I)">
        <ItalicIcon />
      </MarkToolbarButton>
      <ToolbarButton
        pressed={block === KEYS.h2}
        tooltip="Heading"
        onClick={() => editor.tf.toggleBlock(KEYS.h2)}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Heading2Icon />
      </ToolbarButton>
      <ToolbarButton
        pressed={block === KEYS.h3}
        tooltip="Subheading"
        onClick={() => editor.tf.toggleBlock(KEYS.h3)}
        onMouseDown={(e) => e.preventDefault()}
      >
        <Heading3Icon />
      </ToolbarButton>
      <ToolbarButton
        pressed={bulleted}
        tooltip="Bulleted list"
        onClick={() => toggleList(editor, { listStyleType: "disc" })}
        onMouseDown={(e) => e.preventDefault()}
      >
        <ListIcon />
      </ToolbarButton>
      <ToolbarButton
        pressed={numbered}
        tooltip="Numbered list"
        onClick={() => toggleList(editor, { listStyleType: "decimal" })}
        onMouseDown={(e) => e.preventDefault()}
      >
        <ListOrderedIcon />
      </ToolbarButton>
    </ToolbarGroup>
  );
};
